import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { apiClient, handleApiError } from '@/infrastructure/api/client'
import { resolveLanguage } from '@/application/i18n/language'
import type { Investigation, UserProfile, InvestigationTeamMember } from '@/domains/types'

const INVESTIGATION_API_BASE = '/investigations'
const USER_API_BASE = '/users'

// ─── Profile / Me ─────────────────────────────────────────────────────────────

export function useGetMe(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['users', 'me'],
    queryFn: async (): Promise<UserProfile> => {
      const response = await apiClient.get<UserProfile>(`${USER_API_BASE}/me`)
      return response.data
    },
    staleTime: 1000 * 60 * 5,
    enabled: options?.enabled ?? true,
  })
}

export function useUpdateMe() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      displayName?: string | null
      firstName?: string | null
      lastName?: string | null
      email?: string | null
      avatarUrl?: string | null
      biography?: string | null
    }): Promise<UserProfile> => {
      const response = await apiClient.patch<UserProfile>(`${USER_API_BASE}/me`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', 'me'] })
    },
    onError: (error) => {
      throw new Error(handleApiError(error))
    },
  })
}

// ─── My investigations (investigations where the current user is a team member or author) ──────

export function useGetMyInvestigations() {
  const { i18n } = useTranslation()
  const locale = resolveLanguage(i18n.language)
  return useQuery({
    // Locale-scoped for the same reason as investigationKeys.byId/read in
    // investigation.service.ts — a language switch must not serve a
    // previous locale's translated fields from the TanStack cache.
    queryKey: ['investigations', 'my', locale],
    queryFn: async (): Promise<Investigation[]> => {
      const response = await apiClient.get<Investigation[]>(`${INVESTIGATION_API_BASE}/my`, {
        params: { lang: locale },
      })
      return response.data
    },
    staleTime: 1000 * 60 * 5,
  })
}

// ─── Team members ─────────────────────────────────────────────────────────────

export function useGetTeamMembers(investigationId: string) {
  return useQuery({
    queryKey: ['investigations', investigationId, 'team'],
    queryFn: async (): Promise<
      Array<{
        userId: string
        username: string | null
        displayName: string | null
        avatarUrl: string | null
        biography: string | null
        role: string
        addedAt: string
      }>
    > => {
      const response = await apiClient.get<
        Array<{
          userId: string
          username: string | null
          displayName: string | null
          avatarUrl: string | null
          biography: string | null
          role: string
          addedAt: string
        }>
      >(`${INVESTIGATION_API_BASE}/${investigationId}/team`)
      return response.data
    },
    enabled: !!investigationId,
    staleTime: 1000 * 60 * 2,
  })
}

export function useAddTeamMember(investigationId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ userId }: { userId: string }): Promise<InvestigationTeamMember> => {
      const response = await apiClient.post<InvestigationTeamMember>(
        `${INVESTIGATION_API_BASE}/${investigationId}/team`,
        { userId },
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['investigations', investigationId, 'team'],
      })
    },
    onError: (error) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useRemoveTeamMember(investigationId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (userId: string): Promise<void> => {
      await apiClient.delete(`${INVESTIGATION_API_BASE}/${investigationId}/team/${userId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['investigations', investigationId, 'team'],
      })
    },
    onError: (error) => {
      throw new Error(handleApiError(error))
    },
  })
}

// ─── 2FA Management ──────────────────────────────────────────────────────────

export function useSetup2FA() {
  return useMutation({
    mutationFn: async (): Promise<{
      qrCodeDataUrl: string
      otpauthUrl: string
      secret: string
    }> => {
      const response = await apiClient.post<{
        qrCodeDataUrl: string
        otpauthUrl: string
        secret: string
      }>('/auth/2fa/setup')
      return response.data
    },
  })
}

export function useEnable2FA() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (totp: string): Promise<{ success: boolean }> => {
      const response = await apiClient.post<{ success: boolean }>('/auth/2fa/enable', { totp })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', 'me'] })
    },
    onError: (error) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useDisable2FA() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (totp: string): Promise<{ success: boolean }> => {
      const response = await apiClient.post<{ success: boolean }>('/auth/2fa/disable', { totp })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', 'me'] })
    },
    onError: (error) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useDeleteMe() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (): Promise<{ success: boolean; message?: string }> => {
      const response = await apiClient.delete<{ success: boolean; message?: string }>(
        `${USER_API_BASE}/me`,
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.clear()
    },
    onError: (error) => {
      throw new Error(handleApiError(error))
    },
  })
}

// ─── User search ──────────────────────────────────────────────────────────────

export function useSearchUsers(query: string) {
  return useQuery({
    queryKey: ['users', 'search', query],
    queryFn: async (): Promise<
      Array<{
        userId: string
        username: string | null
        displayName: string | null
        role: string
      }>
    > => {
      const response = await apiClient.get<
        Array<{
          userId: string
          username: string | null
          displayName: string | null
          role: string
        }>
      >(`${USER_API_BASE}/search`, {
        params: { q: query },
      })
      return response.data
    },
    enabled: query.length >= 2,
    staleTime: 1000 * 30,
  })
}

export function useGetInvestigators() {
  return useQuery({
    queryKey: ['users', 'investigators'],
    queryFn: async (): Promise<
      Array<{
        id: string
        username: string | null
        displayName: string | null
        avatarUrl: string | null
        biography: string | null
      }>
    > => {
      const response = await apiClient.get('/users/investigators')
      return response.data
    },
    staleTime: 1000 * 60 * 5,
  })
}
